import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';
import { Page, ResponseError } from '../type/type';
import { isAxiosError } from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { CssContentPreviewRequest } from '../type/cssTemplate';

// 상품 파일 타입
interface ProductFile {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  originalFileName: string;
  storedFileName: string;
  fileType: string;
  fileSize: number;
  filePath: string;
  fileFullUrl: string;
}

// 상품 타입
export interface IProduct {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  productName: string;
  productCode: string;
  custInfoCount: 1 | 2;
  description: string;
  price: number;
  discountPrice: number;
  confirmYn: 'Y' | 'N';
  displayYn: 'Y' | 'N';
  thumbnailFile: ProductFile;
  inlineFile: ProductFile;
  detailFiles: ProductFile[];
  sampleFile: ProductFile;
  endFile?: ProductFile;
  franchiseId: string;
  franchiseName: string;
  basicThumbnailFile: ProductFile;
  basicInlineFile: ProductFile;
  basicEndFile?: ProductFile;
  categoryName?: string;
}

export interface IProduct {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  productName: string;
  productCode: string;
  custInfoCount: 1 | 2;
  description: string;
  price: number;
  discountPrice: number;
  confirmYn: 'Y' | 'N';
  displayYn: 'Y' | 'N';
  thumbnailFile: ProductFile;
  inlineFile: ProductFile;
  detailFiles: ProductFile[];
  sampleFile: ProductFile;
  endFile?: ProductFile;
  franchiseId: string;
  franchiseName: string;
  basicThumbnailFile: ProductFile;
  basicInlineFile: ProductFile;
  basicEndFile?: ProductFile;
  categoryName?: string;
}

export type BasicImage = {
  thumbnailFile: ProductFile;
  inlineFile: ProductFile;
  endFile?: ProductFile;
}

interface previewHtml {
  html: string;
}

// 상품 목록 응답 타입
interface ProductResponse extends Page {
  content: IProduct[];
}

// 상품 상세 응답 타입
interface ProductDetailResponse extends IProduct {}

// 상품 등록 요청 타입
export type PostProductRequest = {
  productCode: string;
  productName: string;
  custInfoCount: 1 | 2;
  price: number;
  discountPrice?: number;
  description: string;
  thumbnailFileId: string;
  inlineFileId: string;
  endFileId?: string;
  sampleFileId?: string;
  detailFileIds: string[];
  categoryName: string;
};

// 상품 업데이트 요청 타입
export type UpdateProductRequest = {
  custInfoCount?: 1 | 2;
  price?: number;
  discountPrice?: number;
  description?: string;
  thumbnailFileId?: string;
  inlineFileId?: string;
  detailFileIds?: string[];
  confirmYn?: 'Y' | 'N';
  displayYn?: 'Y' | 'N';
  categoryName?: string;
  sampleFileId?: string;
  endFileId?: string;
};

export type UpdateProductImageRequest = {
  thumbnailFileId?: string;
  inlineFileId?: string;
  endFileId?: string;
  detailFileIds?: string[];
};

export type categories = {
  id: string;
  categoryName: string;
}

// Zustand Store Data 타입
type ProductData = {
  products: IProduct[] | null;
  productDetail: IProduct | null;
  previewHtml: string | null;
  basicImage: BasicImage | null;
};

// Zustand Store Action 타입
type ProductAction = {
  // 상품 목록 가져오기
  getProducts: (
    productName?: string,
    field?: string,
    order?: 'asc' | 'desc',
    categoryId?: string,
  ) => void;

  // 상품 상세 가져오기
  getProductDetail: (id: string) => void;

  getProductPreview: (
    id: string,
    thumbnailId: string,
    inlineId: string,
    endId?: string,
    data?: {
      fontMapper?: any;
      cssContents?: CssContentPreviewRequest[];
    }
  ) => Promise<string>;

  getProductBasicImage: (id: string) => void;

  // 상품 등록
  postProduct: (
    productData: PostProductRequest,
    navigate: NavigateFunction
  ) => Promise<void>;

  postProductBasicImageUpdate: (
      productId: string,
      thumbnailFileId?: string,
      inlineFileId?: string,
      endFileId?: string
  ) => void

  // 상품 업데이트
  updateProduct: (
    id: string,
    productData: UpdateProductRequest
  ) => Promise<void>;

  updateProductImage: (
      id: String,
      productData: UpdateProductImageRequest
  ) => Promise<void>;

  // 상품 활성화
  activeProduct: (id: string, confirmYn: 'Y' | 'N') => Promise<void>;

  getCategories: () => Promise<categories[]>;
  
  searchCategories: (categoryName: string) => Promise<categories[]>;
};

type ProductByFranchiseAction = {
  // 상품 목록 가져오기
  getProducts: (
    franchiseId?: string,
    order?: 'asc' | 'desc',
  ) => void;

  // 상품 상세 가져오기
  getProductDetail: (id: string, franchiseId: String) => void;

  // 상품 등록
  postProduct: (
    productData: PostProductRequest,
    navigate: NavigateFunction
  ) => Promise<void>;

  // 상품 업데이트
  updateProduct: (
    id: string,
    productData: UpdateProductRequest
  ) => Promise<void>;

  // 상품 활성화
  activeProduct: (id: string, confirmYn: 'Y' | 'N') => Promise<void>;
  
  // 상품 목록 초기화
  clearProducts: () => void;
};

export const useProductStore = create<ProductData & ProductAction>(set => ({
  // 상품 목록
  products: null,

  // 상품 상세
  productDetail: null,

  // 미리보기 HTML
  previewHtml: null,
  
  // 기본 이미지
  basicImage: null,

  // 상품 목록 가져오기
  getProducts: async (productName, field, order, categoryId) => {
    set({ productDetail: null }); // 상품 상세 초기화

    const sort = field && order && `${field},${order}`;
    try {
      const response = await apiClient.get<ProductResponse>('/product', {
        params: {
          productName,
          sort,
          size: 1000,
          categoryId
        },
      });

      set({ products: response.data.content }); // 상품 목록 세팅
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('상품 목록을 가져오지 못했습니다.');
    }
  },

  // 상품 상세 가져오기
  getProductDetail: async id => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.get<ProductDetailResponse>(
        `/product/${id}`
      );
      set({ productDetail: response.data });
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('상품 상세를 가져오지 못했습니다.');
    }
  },


  getProductPreview: async (id, thumbnailId, inlineId, endId, data?) => {
    try {
      const params = new URLSearchParams({
        thumbnailId: thumbnailId,
        inlineId: inlineId,
        ...(endId && { endId: endId })
      } as Record<string, string>);
      const requestBody: Record<string, any> = {};
      const cssContents = data?.cssContents;
      if (cssContents && cssContents.length > 0) {
        requestBody.cssContents = cssContents;
      } else {
        requestBody.cssContents = [];
      }
      if (data?.fontMapper) {
        requestBody.fontMapper = data.fontMapper;
      }

      const response = await apiClient.post<string>(
        `/product/preview/${id}?${params.toString()}`,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('상품 미리보기를 가져오지 못했습니다.');
      return '';
    }
  },

  getProductBasicImage: async (id: string) => {
    try {
      const response = await apiClient.get<BasicImage>(`/product/img/basic/${id}`);
      set({ basicImage: response.data });
      return response.data;
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('기본 이미지를 가져오지 못했습니다.');
    }
  },

  // 상품 등록
  postProduct: async (productData, navigate) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.post('/product', productData);
      if (response.status === 200) {
        toast.success('상품이 성공적으로 등록되었습니다!');
        navigate('/product');
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 등록 실패:', error);
        toast.error('상품 등록에 실패했습니다.');
      }
    }
  },

  // 기본이미지 수정
  postProductBasicImageUpdate: async (productId, thumbnailFileId, inlineFileId, endFileId?) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.patch(`/product/img/basic/${productId}`, {
        thumbnailFileId: thumbnailFileId,
        inlineFileId: inlineFileId,
        endFileId: endFileId
      });
      if (response.status === 200) {
        toast.success('기본 이미지가 변경되었습니다!');
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 등록 실패:', error);
        toast.error('상품 등록에 실패했습니다.');
      }
    }
  },

  // 상품 업데이트
  updateProduct: async (id, promptData) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.post(
        `/product/update/${id}`,
        promptData
      );
      if (response.status === 200) {
        // 성공 시 컴포넌트에서 toast 처리
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 수정 실패:', error);
        toast.error('상품 수정에 실패했습니다.');
      }
    }
  },

  updateProductImage: async (id, promptData) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.post(
          `/product/update-image/${id}`,
          promptData
      );
      if (response.status === 200) {
        // 성공 시 컴포넌트에서 toast 처리
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 수정 실패:', error);
        toast.error('상품 수정에 실패했습니다.');
      }
    }
  },

  // 상품 활성화
  activeProduct: async (id, confirmYn) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.post(`/product/update/${id}`, {
        confirmYn,
      });
      if (response.status === 200) {
        if (confirmYn === 'Y') {
          toast.success('상품이 성공적으로 활성화 되었습니다!');
        } else {
          toast.success('상품이 성공적으로 비활성화 되었습니다!');
        }
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 활성화 실패:', error);
        toast.error('상품 활성화 핸들링에 실패했습니다.');
      }
    }
  },
  getCategories: async () => {
    try {
      const response = await apiClient.get<categories[]>(`/category`);
      if (response.status === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('카테고리 조회 실패:', error);
        toast.error('카테고리 조회에 실패하였습니다.');
      }
      return [];
    }
  },
  searchCategories: async (categoryName) => {
    try {
      const response = await apiClient.get<categories[]>(`/category`, {
        params: {
          categoryName: categoryName
        }
      });
      if (response.status === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('카테고리 검색 실패:', error);
      return [];
    }
  }
}));



export const useProductByFranchiseStore = create<ProductData & ProductByFranchiseAction>(set => ({
  // 상품 목록
  products: null,

  // 상품 상세
  productDetail: null,

  // 미리보기 HTML
  previewHtml: null,
  
  // 기본 이미지
  basicImage: null,

  // 상품 목록 가져오기
  getProducts: async (franchiseId, order) => {
    set({ productDetail: null }); // 상품 상세 초기화

    try {
      let response;

      // field가 franchiseId인 경우 다른 API 엔드포인트 사용
        response = await apiClient.get<ProductResponse>(`/product/franchise`, {
          params: {
            franchiseId: franchiseId,
            sort: order,
            size: 100,
          },
        });


      set({ products: response.data.content }); // 상품 목록 세팅
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('상품 목록을 가져오지 못했습니다.');
    }
  },

  // 상품 상세 가져오기
  getProductDetail: async (id, franchiseId) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.get<ProductDetailResponse>(
          `/product/${id}/${franchiseId}`
      );
      set({ productDetail: response.data });
    } catch (error) {
      console.error('API 요청 실패:', error);
      toast.error('상품 상세를 가져오지 못했습니다.');
    }
  },

  // 상품 등록
  postProduct: async (productData, navigate) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.post('/product', productData);
      if (response.status === 200) {
        toast.success('상품이 성공적으로 등록되었습니다!');
        navigate('/product');
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 등록 실패:', error);
        toast.error('상품 등록에 실패했습니다.');
      }
    }
  },

  // 상품 업데이트
  updateProduct: async (id, promptData) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.post(
          `/product/update/${id}`,
          promptData
      );
      if (response.status === 200) {
        // 성공 시 컴포넌트에서 toast 처리
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 수정 실패:', error);
        toast.error('상품 수정에 실패했습니다.');
      }
    }
  },
  // 상품 활성화
  activeProduct: async (id, confirmYn) => {
    set({ products: null }); // 상품 목록 초기화

    try {
      const response = await apiClient.post(`/product/update/${id}`, {
        confirmYn,
      });
      if (response.status === 200) {
        if (confirmYn === 'Y') {
          toast.success('상품이 성공적으로 활성화 되었습니다!');
        } else {
          toast.success('상품이 성공적으로 비활성화 되었습니다!');
        }
      }
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg);
      } else {
        console.error('상품 활성화 실패:', error);
        toast.error('상품 활성화 핸들링에 실패했습니다.');
      }
    }
  },
  
  // 상품 목록 초기화
  clearProducts: () => {
    set({ products: null });
  },
}));
